import type { AbstractPowerSyncDatabase } from "@powersync/common";
import { USER_DATA_TABLES } from "@bodega/powersync";

/**
 * Replaces VISITOR_ID with the newly authenticated user's ID
 * across all relevant tables and the offline CRUD queue.
 *
 * This ensures that:
 * 1. The local UI instantly reflects the new owner without flashing.
 * 2. The PowerSync upload queue sends the correct `user_id` to the backend.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export async function sweepVisitorData(
  db: AbstractPowerSyncDatabase | null,
  newUserId: string,
  visitorId: string,
) {
  if (!db || !newUserId) {
    return;
  }

  try {
    // 1. Update the actual table rows so UI queries pick them up immediately
    await Promise.all(
      USER_DATA_TABLES.map(async (table) =>
        db.execute(`UPDATE ${table} SET user_id = ? WHERE user_id = ?`, [newUserId, visitorId]),
      ),
    );

    // 2. Rewrite the queued operations so the network payload has the correct user_id.
    //
    // PowerSync's `ps_crud.data` column is a JSON envelope
    // `{"op":"PUT","type":"<table>","id":"<uuid>","data":{<column>:<value>,…}}`
    // — the row's ACTUAL column values live NESTED under `$.data.*`, not at the
    // top level (see `CrudEntry.fromRow`: `opData = parsed.data`). So the visitor
    // id must be rewritten at `$.data.user_id`; the previous top-level `$.user_id`
    // path matched NOTHING, leaving the queued op stamped with `VISITOR_ID`.
    //
    // For user_id-scoped writes the server derives ownership from the JWT `sub`
    // (so a stale payload id was tolerated), but org-scoped/shared surfaces
    // authorize by the payload → a queued PUT still carrying `VISITOR_ID` fails
    // the check and is silently dropped (200, inserts nothing). Guard the correct
    // nested JSON path.
    await db.execute(
      `
      UPDATE ps_crud
      SET data = json_set(data, '$.data.user_id', ?)
      WHERE json_extract(data, '$.data.user_id') = ?
    `,
      [newUserId, visitorId],
    );
  } catch (error) {
    console.error("[Sweep] Failed to sweep visitor data:", error);
  }
}
